import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ProductItem from "./components/ProductItem";
import { useCart } from "../../hooks/useCart";
import { useCategories } from "../../hooks/useCategories";
import { useAllServices } from "../../hooks/useServices";
import { useAllSubscriptions } from "../../hooks/useSubscriptions";
import { useCurrentSession } from "../../hooks/useSession";
import QueryStateWrapper from "@/components/ui/QueryStateWrapper";
import { toast } from "react-toastify";
import "react-lazy-load-image-component/src/effects/blur.css";

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import Button from "@/components/ui/Button";
import { FaCarSide, FaMotorcycle } from "react-icons/fa6";
import Icon from "@mdi/react";
import { mdiCarHatchback, mdiCarEstate, mdiBusSchool } from "@mdi/js";

interface HomeItem {
  id: string; // تغيير من number إلى string لدعم الـ key الفريد
  name: string;
  price: string;
  icon: string;
  category: number;
  type: "service" | "subscription";
  price_id: number;
  is_quantity_requestable?: boolean;
}

const sizes = [
  { key: "motorcycle", label: "دراجة نارية", icon: <FaMotorcycle /> },
  {
    key: "small",
    label: "سيارة S",
    icon: <FaCarSide />,
  },
  {
    key: "medium",
    label: "سيارة M",
    icon: <Icon path={mdiCarHatchback} size={1.5} />,
  },
  {
    key: "large",
    label: "سيارة L",
    icon: <Icon path={mdiCarEstate} size={1.5} />,
  },
  {
    key: "extra_large",
    label: "سيارة XL",
    icon: <Icon path={mdiBusSchool} size={1.5} />,
  },
  {
    key: "all",
    label: "الكل",
    icon: <Icon path={mdiBusSchool} size={1.5} />,
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { cart: order, addItem, removeItem, setOrderData } = useCart();

  // فحص حالة الجلسة
  const { data: sessionData, isLoading: isLoadingSession } =
    useCurrentSession();

  // فحص إذا كانت الجلسة منتهية أو غير موجودة → التوجيه إلى صفحة بدء الجلسة بدون تسجيل خروج تلقائي
  useEffect(() => {
    const sessionStarted = localStorage.getItem("sessionStarted");
    // إذا انتهى التحميل ولم توجد جلسة نشطة وتم بدء جلسة محليًا
    if (!isLoadingSession && !sessionData && sessionStarted) {
      toast.error("لا توجد جلسة نشطة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }

    // إذا كانت الجلسة موجودة لكنها منتهية
    if (sessionData && sessionData.ended_at && sessionStarted) {
      toast.error("انتهت الجلسة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }
  }, [sessionData, isLoadingSession, navigate]);

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useCategories();

  const {
    data: servicesData,
    isLoading: servicesLoading,
    error: servicesError,
  } = useAllServices();

  const {
    data: subscriptionsData,
    isLoading: subscriptionsLoading,
    error: subscriptionsError,
  } = useAllSubscriptions();

  const categories = Array.isArray(categoriesData) ? categoriesData : [];
  const services = Array.isArray(servicesData) ? servicesData : [];
  const subscriptions = Array.isArray(subscriptionsData) ? subscriptionsData : [];

  // عرض التصنيفات التي تحتوي على خدمات أو اشتراكات فقط
  const categoriesWithItems = categories.filter((cat) => {
    const hasServices = services.some((s: any) => s.category === cat.id);
    const hasSubscriptions = subscriptions.some(
      (sub: any) => sub.category === cat.id
    );
    return hasServices || hasSubscriptions;
  });

  const VISIBLE_CATEGORIES = categoriesWithItems;

  useEffect(() => {
    if (VISIBLE_CATEGORIES.length > 0) {
      const categoriesWithImages = VISIBLE_CATEGORIES.filter(
        (cat) => cat.image
      );

      if (categoriesWithImages.length > 0) {
        categoriesWithImages.forEach((cat) => {
          const img = new Image();
          img.onload = () => {};
          img.onerror = () => {};
          img.src = cat.image!;
        });
      }
    }
  }, [VISIBLE_CATEGORIES, categoriesData]);

  const [activeSection, setActiveSection] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<string>("motorcycle");

  useEffect(() => {
    if (VISIBLE_CATEGORIES.length > 0 && !activeSection) {
      setActiveSection(VISIBLE_CATEGORIES[0].id.toString());
    } else if (VISIBLE_CATEGORIES.length > 0 && activeSection) {
      // التأكد من أن التصنيف النشط لا يزال موجوداً في التصنيفات المرئية
      const isActiveSectionValid = VISIBLE_CATEGORIES.some(
        (cat) => cat.id.toString() === activeSection
      );
      if (!isActiveSectionValid) {
        setActiveSection(VISIBLE_CATEGORIES[0].id.toString());
      }
    }
  }, [VISIBLE_CATEGORIES, activeSection]);

  const sizeLabelMap: Record<string, string> = {
    motorcycle: "دراجة نارية",
    small: "سيارة S",
    medium: "سيارة M",
    large: "سيارة L",
    extra_large: "سيارة XL",
  };

  // بناء عناصر الخدمات لكل تصنيف
  const serviceItems: HomeItem[] = categories.flatMap((cat) => {
    // خدمات التصنيف الحالي
    const categoryServices = services.filter((s: any) => s.category === cat.id);
    // فلترة الأحجام
    return categoryServices.flatMap((s: any) => {
      const list =
        selectedSize === "all"
          ? s.prices
          : s.prices?.filter((p: any) => p.size === selectedSize);
      if (!list) return [];
      return list.map((p: any) => ({
        id: `${s.id}-${p.id}-${cat.id}-${p.size}`, // مفتاح فريد لكل عنصر
        name: `${s.name} ${
          selectedSize === "all" ? sizeLabelMap[p.size] ?? "" : ""
        }`.trim(),
        price: p.final_price.toString(),
        icon: s.image || "",
        category: cat.id,
        type: "service" as const,
        price_id: p.id,
        is_quantity_requestable: s.is_quantity_requestable,
      }));
    });
  });

  // بناء عناصر الاشتراكات لكل تصنيف
  const subscriptionItems: HomeItem[] = categories.flatMap((cat) => {
    const categorySubscriptions = subscriptions.filter(
      (sub: any) => sub.category === cat.id
    );
    return categorySubscriptions.flatMap((sub: any) => {
      const list =
        selectedSize === "all"
          ? sub.prices
          : sub.prices?.filter((p: any) => p.size === selectedSize);
      if (!list) return [];
      return list.map((p: any) => ({
        id: `${sub.id}-${p.id}-${cat.id}-${p.size}`, // مفتاح فريد لكل عنصر
        name: `${sub.name} ${
          selectedSize === "all" ? sizeLabelMap[p.size] ?? "" : ""
        }`.trim(),
        price: p.final_price.toString(),
        icon: sub.image || "",
        category: cat.id,
        type: "subscription" as const,
        price_id: p.id,
      }));
    });
  });

  /* عرض جميع العناصر (خدمات واشتراكات) في التصنيف المحدد */
  const allItems: HomeItem[] = [...serviceItems, ...subscriptionItems];

  // إزالة العناصر المكررة بناءً على الـ ID النهائي
  const uniqueItems: HomeItem[] = [];
  const seenIds = new Set<string>();

  allItems.forEach((item) => {
    if (!seenIds.has(item.id)) {
      seenIds.add(item.id);
      uniqueItems.push(item);
    }
  });

  const filteredItems = uniqueItems.filter((item) => {
    const matchesCategory =
      activeSection && categories.some((c) => String(c.id) === activeSection)
        ? String(item.category) === activeSection
        : true;

    return matchesCategory;
  });

  // دالة مساعدة لتحديد ما إذا كان العنصر محدداً في السلة
  const isItemSelected = (item: HomeItem): boolean => {
    // استخراج الـ ID الأصلي للخدمة (بدون الحجم) للمقارنة مع السلة
    const originalServiceId = item.id.replace(/-\w+$/, ""); // إزالة الحجم من النهاية
    return order.some((o) => o.id === originalServiceId);
  };

  const handleItemClick = (item: HomeItem) => {
    const isSelected = isItemSelected(item);
    // استخراج الـ ID الأصلي للخدمة (بدون الحجم)
    const originalServiceId = item.id.replace(/-\w+$/, ""); // إزالة الحجم من النهاية
    if (isSelected) {
      removeItem(originalServiceId);
    } else {
      if (item.type === "subscription") {
        const existingSub = order.find((o) => o.type === "subscription");
        if (existingSub) {
          toast.error(
            "يمكنك اختيار اشتراك واحد فقط، قم بإزالة الاشتراك الحالي أولاً"
          );
          return;
        }
      }

      // منع اختيار أكثر من خدمة واحدة في نفس الوقت (فقط للخدمات التي لا تسمح بالتعدد)
      if (item.type === "service" && !item.is_quantity_requestable) {
        const existingService = order.find(
          (o) => o.type === "service" && !(o as any).is_quantity_requestable
        );
        if (existingService) {
          toast.error(
            "يمكنك اختيار خدمة واحدة فقط في الطلب، قم بإزالة الخدمة الحالية أولاً"
          );
          return;
        }
      }

      setOrderData(null);

      const cartItem = {
        ...item,
        id: originalServiceId, // استخدام الـ ID الأصلي في السلة
        quantity: 1,
        type: item.type,
        price_id: item.price_id,
        is_quantity_requestable: item.is_quantity_requestable,
      };

      addItem(cartItem);
    }
  };

  const totalQuantity = order.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = order.reduce(
    (sum, i) => sum + Number(i.price) * i.quantity,
    0
  );

  const hasErrors = categoriesError || servicesError || subscriptionsError;
  const isLoading =
    categoriesLoading || servicesLoading || subscriptionsLoading;


  const availableSizes = useMemo(() => {
    const checkSizeExists = (sizeKey: string) => {
      const matchCategory = (cat: number) =>
        activeSection ? String(cat) === activeSection : true;

      const inServices = services.some(
        (s: any) =>
          matchCategory(s.category) &&
          (sizeKey === "all"
            ? s.prices?.length > 0
            : s.prices?.some((p: any) => p.size === sizeKey))
      );

      const inSubscriptions = subscriptions.some(
        (sub: any) =>
          matchCategory(sub.category) &&
          (sizeKey === "all"
            ? sub.prices?.length > 0
            : sub.prices?.some((p: any) => p.size === sizeKey))
      );

      return inServices || inSubscriptions;
    };

    return sizes.filter((sz) => sz.key === "all" || checkSizeExists(sz.key));
  }, [services, subscriptions, activeSection, sizes]);

  // إذا الحجم الحالي غير متاح نختار أول حجم متاح أو 'all'
  useEffect(() => {
    if (!availableSizes.some((s) => s.key === selectedSize)) {
      setSelectedSize("all");
    }
  }, [availableSizes, selectedSize]);

  return (
    <QueryStateWrapper isLoading={isLoading} isError={!!hasErrors}>
      <div className='p-4 max-w-5xl mx-auto'>
        {/* تحقق من وجود التصنيفات */}
        {categories.length === 0 && !isLoading && (
          <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6'>
            <div className='flex items-center'>
              <div className='text-yellow-600 text-xl mr-3'>⚠️</div>
              <div>
                <h3 className='text-yellow-800 font-bold'>لا توجد تصنيفات</h3>
                <p className='text-yellow-700 text-sm'>
                  لم يتم العثور على أي تصنيفات. تحقق من اتصال الشبكة أو البيانات
                  في الخادم.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* التصنيفات */}
        {VISIBLE_CATEGORIES.length > 0 && (
          <>
            {/* عرض التصنيفات كـ cards إذا كانت 5 أو أقل */}
            {VISIBLE_CATEGORIES.length <= 5 ? (
              <div className='grid grid-cols-5 gap-2 sm:gap-3 md:gap-4 mb-6'>
                {VISIBLE_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveSection(cat.id.toString())}
                    className={`p-2 sm:p-2 md:p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center justify-center text-center min-h-[90px] sm:min-h-[100px] md:min-h-[110px] ${
                      activeSection === cat.id.toString()
                        ? " bg-red-600 text-white shadow-lg"
                        : "bg-white border-gray-300 text-gray-700 hover:border-red-500 hover:bg-red-50 hover:shadow-md transform hover:scale-105"
                    }`}
                  >
                    <div className='w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 mb-2 sm:mb-3 flex items-center justify-center bg-white border border-gray-200 rounded-lg overflow-hidden'>
                      {cat.image ? (
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className='w-full h-full object-contain p-0.5'
                        />
                      ) : (
                        <div className='w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center text-gray-400'>
                          <svg
                            width='24'
                            height='24'
                            viewBox='0 0 24 24'
                            fill='currentColor'
                            className='drop-shadow-sm'
                          >
                            <path d='M21,19V5c0,-1.1 -0.9,-2 -2,-2H5c-1.1,0 -2,0.9 -2,2v14c0,1.1 0.9,2 2,2h14c1.1,0 2,-0.9 2,-2zM8.5,13.5l2.5,3.01L14.5,12l4.5,6H5l3.5,-4.5z' />
                          </svg>
                        </div>
                      )}
                    </div>
                    <span className='text-sm sm:text-base font-bold leading-tight'>
                      {cat.name}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              /* عرض التصنيفات كـ dropdown إذا كانت أكثر من 5 */
              <div className='mb-6'>
                <Select value={activeSection} onValueChange={setActiveSection}>
                  <SelectTrigger
                    className='w-full bg-white border border-gray-300 text-right hover:bg-gray-50 hover:border-red-500 transition-colors duration-200'
                    dir='rtl'
                  >
                    <SelectValue
                      placeholder='اختر التصنيف'
                      className='text-right'
                    />
                  </SelectTrigger>
                  <SelectContent
                    className='bg-white [&>*]:text-center [&_svg]:order-last [&_div]:text-center [&_div]:w-full'
                    dir='rtl'
                  >
                    {VISIBLE_CATEGORIES.map((cat, index) => (
                      <div key={cat.id}>
                        <SelectItem
                          value={cat.id.toString()}
                          className='text-center hover:bg-red-50 hover:text-red-700 cursor-pointer transition-colors duration-200 w-full'
                        >
                          <div className='flex items-center gap-2 justify-center w-full text-center'>
                            <span className='flex-1 text-center'>
                              {cat.name}
                            </span>
                            {cat.image ? (
                              <img
                                src={cat.image}
                                alt={cat.name}
                                className='w-6 h-6 object-contain flex-shrink-0'
                              />
                            ) : (
                              <div className='w-6 h-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded flex items-center justify-center flex-shrink-0'>
                                <svg
                                  width='16'
                                  height='16'
                                  viewBox='0 0 24 24'
                                  fill='currentColor'
                                  className='text-gray-400'
                                >
                                  <path d='M21,19V5c0,-1.1 -0.9,-2 -2,-2H5c-1.1,0 -2,0.9 -2,2v14c0,1.1 0.9,2 2,2h14c1.1,0 2,-0.9 2,-2zM8.5,13.5l2.5,3.01L14.5,12l4.5,6H5l3.5,-4.5z' />
                                </svg>
                              </div>
                            )}
                          </div>
                        </SelectItem>
                        {index < VISIBLE_CATEGORIES.length - 1 && (
                          <hr className='border-t border-gray-200 mx-2' />
                        )}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        )}

        {/* عرض باقي العناصر فقط إذا كانت هناك تصنيفات مرئية */}
        {VISIBLE_CATEGORIES.length > 0 ? (
          <>
            <hr className='border-t border-gray-300 my-6' />

            {/* الأحجام */}
            <div className='grid grid-cols-6 gap-2 sm:gap-3 md:gap-4 mb-4'>
              {availableSizes.map((size) => (
                <button
                  key={size.key}
                  onClick={() => setSelectedSize(size.key)}
                  className={`px-3 py-2 sm:px-4 sm:py-4  rounded-xl border text-sm sm:text-md font-bold transition-all duration-200
                      ${
                        selectedSize === size.key
                          ? "bg-red-100 border-red-600 text-red-600 flex-col items-center justify-center shadow-lg"
                          : "bg-white border-gray-300 text-gray-700 hover:border-red-500 hover:bg-red-50 flex-col items-center justify-center hover:shadow-md transform hover:scale-105"
                      }
                    `}
                >
                  <span className='text-2xl sm:text-3xl flex items-center justify-center text-red-700'>
                    {size.icon}
                  </span>
                  {size.label}
                </button>
              ))}
            </div>

            <hr className='border-t border-gray-300 my-6' />

            {/* المنتجات */}
            <ProductItem
              items={filteredItems.map((item) => ({
                id: item.id,
                name: item.name,
                final_price: Number(item.price),
                icon: item.icon,
                category: item.category,
              }))}
              emptyMessage='لا توجد عناصر'
              onItemClick={(item) => {
                const fullItem = filteredItems.find((fi) => fi.id === item.id);
                if (fullItem) handleItemClick(fullItem);
              }}
              selectedIds={filteredItems
                .filter((item) => isItemSelected(item))
                .map((item) => item.id)}
            />
          </>
        ) : (
          /* رسالة عندما لا توجد تصنيفات مرئية */
          <div className='flex flex-col items-center justify-center py-12 text-gray-500'>
            <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='currentColor'
              >
                <path d='M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,5V19H5V5H19Z' />
              </svg>
            </div>
            <h3 className='text-lg font-semibold mb-2'>
              لا توجد تصنيفات متاحة
            </h3>
            <p className='text-sm text-center'>
              لا توجد خدمات أو اشتراكات متاحة حالياً
            </p>
          </div>
        )}

        {/* زر الطلب */}
        {order.length > 0 && (
          <Button
            className='w-full bg-red-700 text-white rounded-2xl py-4 mt-10 text-xl md:text-2xl '
            onClick={() => {
              navigate("/cart");
              toast.success("تم إضافة العناصر إلى السلة بنجاح!");
            }}
          >
            تأكيد الطلب ({totalQuantity} صنف {totalPrice} ريال)
          </Button>
        )}
      </div>
    </QueryStateWrapper>
  );
};

export default HomePage;
